import Service, { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { getClient } from 'glimmer-apollo';
import { SEARCH_QUERY, SEARCH_SUGGESTIONS_QUERY } from '../gql/queries/search';
import config from 'embroil/config/environment';

import type {
  SearchRequest,
  SearchResponse,
  SearchResult,
  SearchSuggestion,
  SearchOptions,
  SearchFilters,
  SearchResultType,
} from '../types/search';

import { SearchValidationError, SearchServiceError } from '../types/search';

interface MetricsService {
  track(eventName: string, properties: Record<string, any>): void;
}

export default class SearchService extends Service {
  @service declare metrics: MetricsService;

  @tracked isSearching = false;
  @tracked lastQuery = '';
  @tracked recentSearches: string[] = [];

  // Cache for search results (simple in-memory cache)
  private searchCache = new Map<
    string,
    { response: SearchResponse; timestamp: number }
  >();
  private readonly CACHE_TIMEOUT = 5 * 60 * 1000; // 5 minutes

  constructor(...args: any[]) {
    super(...args);
    this.loadRecentSearches();
  }

  /**
   * Main search method
   */
  async search(
    request: SearchRequest,
    options: SearchOptions = {},
  ): Promise<SearchResponse> {
    const startTime = Date.now();

    try {
      // Validate request
      this.validateSearchRequest(request);

      // Check cache first
      const cacheKey = this.getCacheKey(request);
      if (options.enableCache !== false) {
        const cached = this.getCachedResponse(cacheKey);
        if (cached) {
          this.trackSearchAnalytics(request, cached, startTime, options);
          return cached;
        }
      }

      this.isSearching = true;
      this.lastQuery = request.query;

      // Perform the actual search
      const response = await this.performSearch(request);

      // Cache the response
      if (options.enableCache !== false) {
        this.cacheResponse(cacheKey, response);
      }

      // Track analytics
      this.trackSearchAnalytics(request, response, startTime, options);

      // Update recent searches
      this.addToRecentSearches(request.query);

      return response;
    } catch (error) {
      console.error('Search error:', error);

      // Track error analytics
      if (options.enableAnalytics !== false) {
        this.metrics.track('search_error', {
          query: request.query,
          error: error instanceof Error ? error.message : 'Unknown error',
          user_id: options.userId,
          session_id: options.sessionId,
        });
      }

      throw error;
    } finally {
      this.isSearching = false;
    }
  }

  /**
   * Get search suggestions/autocomplete
   */
  async getSuggestions(
    query: string,
    limit: number = 5,
  ): Promise<SearchSuggestion[]> {
    if (!query || query.length < 2) {
      return [];
    }

    try {
      const client = getClient(this);
      const result = await client.query({
        query: SEARCH_SUGGESTIONS_QUERY,
        variables: { query, limit },
        fetchPolicy: 'cache-first'
      });

      if (result.error) {
        console.warn('Error getting search suggestions:', result.error);
        return [];
      }

      return result.data?.searchSuggestions || [];
    } catch (error) {
      console.warn('Error getting search suggestions:', error);
      return [];
    }
  }

  /**
   * Track when user clicks on a search result
   */
  trackResultClick(
    query: string,
    result: SearchResult,
    position: number,
    options: SearchOptions = {},
  ): void {
    if (options.enableAnalytics === false) return;

    this.metrics.track('search_result_clicked', {
      query,
      result_id: result.id,
      result_type: result.type,
      result_title: result.title,
      position,
      user_id: options.userId,
      session_id: options.sessionId,
    });
  }

  /**
   * Clear search cache
   */
  clearCache(): void {
    this.searchCache.clear();
  }

  /**
   * Get recent searches
   */
  getRecentSearches(): string[] {
    return [...this.recentSearches];
  }

  /**
   * Clear recent searches
   */
  clearRecentSearches(): void {
    this.recentSearches = [];
    if (typeof localStorage !== 'undefined') {
      localStorage.removeItem('1vx-recent-searches');
    }
  }

  // Private methods

  private validateSearchRequest(request: SearchRequest): void {
    if (!request.query || typeof request.query !== 'string') {
      throw new SearchValidationError(
        'Search query is required and must be a string',
      );
    }

    if (request.query.length < 2) {
      throw new SearchValidationError(
        'Search query must be at least 2 characters long',
      );
    }

    if (request.query.length > 500) {
      throw new SearchValidationError(
        'Search query must be less than 500 characters',
      );
    }

    if (request.page && request.page < 1) {
      throw new SearchValidationError('Page must be greater than 0');
    }

    if (request.limit && (request.limit < 1 || request.limit > 100)) {
      throw new SearchValidationError('Limit must be between 1 and 100');
    }
  }

  private async performSearch(request: SearchRequest): Promise<SearchResponse> {
    const page = request.page || 1;
    const limit = request.limit || 20;
    const offset = (page - 1) * limit;

    try {
      const client = getClient(this);
      const result = await client.query({
        query: SEARCH_QUERY,
        variables: {
          query: request.query,
          limit
        },
        fetchPolicy: 'cache-first'
      });

      if (result.error) {
        throw new SearchServiceError(`Search query failed: ${result.error.message}`);
      }

      const searchResponse = result.data?.search;
      if (!searchResponse) {
        throw new SearchServiceError('No search data returned');
      }

      // Transform results to match existing format
      const results = (searchResponse.results || []).map((item: any) =>
        this.transformSearchResult(item, request.query),
      );

      // Since we don't get total count from GraphQL, we estimate based on results
      // If we get fewer results than limit, we're likely on the last page
      const estimatedTotal = results.length < limit ? offset + results.length : (page * limit) + 1;

      // Build response
      const response: SearchResponse = {
        query: request.query,
        total: estimatedTotal,
        page,
        limit,
        results,
        filters: request.filters || {},
      };

      // Add suggestions if requested
      if (request.includeSuggestions && results.length === 0) {
        const suggestionObjects = await this.getSuggestions(request.query);
        response.suggestions = suggestionObjects.map((s) => s.text);
      }

      return response;
    } catch (error) {
      if (error instanceof SearchServiceError) {
        throw error;
      }
      throw new SearchServiceError(
        `Search failed: ${
          error instanceof Error ? error.message : 'Unknown error'
        }`,
      );
    }
  }

  private transformSearchResult(dbResult: any, query: string): SearchResult {
    // Create highlighted fields (basic implementation)
    const highlightedFields = this.createHighlights(dbResult, query);

    // Get the type from __typename
    const type = this.mapGraphQLTypeToSearchType(dbResult.__typename);

    // Base result structure
    const result: SearchResult = {
      id: dbResult.id,
      type,
      title: this.extractTitle(dbResult, type),
      excerpt: this.extractExcerpt(dbResult, type),
      url: this.buildUrl(dbResult, type),
      publishedDate: dbResult.publishedDate || null,
      author: this.extractAuthor(dbResult),
      tags: dbResult.tags || [],
      relevanceScore: 0, // Not provided by GraphQL currently
      highlightedFields,
      category: dbResult.__typename === 'ESportsArticle' ? 'esports' : 
                dbResult.__typename === 'GameArticle' ? 'review' : undefined,
    };

    // Add type-specific fields
    this.addTypeSpecificFields(result, dbResult, type);

    return result;
  }

  private mapGraphQLTypeToSearchType(graphqlType: string): SearchResultType {
    switch (graphqlType) {
      case 'ESportsArticle':
        return 'article';
      case 'GameArticle':
        return 'article';
      case 'Team':
        return 'team';
      case 'Player':
        return 'player';
      case 'Tournament':
        return 'tournament';
      case 'Match':
        return 'match';
      default:
        return 'article';
    }
  }

  private extractTitle(dbResult: any, type: SearchResultType): string {
    if (type === 'player') {
      return dbResult.gamertag || dbResult.irlName || 'Unknown Player';
    } else if (type === 'team') {
      return dbResult.name || 'Unknown Team';
    }
    return dbResult.title || 'Untitled';
  }

  private extractExcerpt(dbResult: any, type: SearchResultType): string {
    if (type === 'team') {
      return dbResult.bioBlurb || '';
    } else if (type === 'player') {
      return `${dbResult.irlName || ''} - ${dbResult.role || 'Player'}`.trim();
    } else if (type === 'match') {
      // Build match description from teams and format
      const teams = dbResult.teams?.map((team: any) => team.name).join(' vs ') || '';
      const format = dbResult.format ? `${dbResult.format}` : '';
      return [teams, format].filter(Boolean).join(' • ');
    }
    return dbResult.shortDescription || '';
  }

  private buildUrl(dbResult: any, type: SearchResultType): string {
    // Build URL from slug/id based on type
    const slug = dbResult.slug || dbResult.id;
    switch (type) {
      case 'team':
        return `/esports/team/${slug}`;
      case 'player':
        return `/esports/player/${slug}`;
      case 'tournament':
        return `/esports/tournament/${slug}`;
      case 'match':
        return `/esports/matches/${slug}`;
      case 'article':
        if (dbResult.__typename === 'GameArticle') {
          return `/games/${slug}`;
        } else if (dbResult.__typename === 'ESportsArticle') {
          return `/esports/news/${slug}`;
        }
        return `/news/${slug}`;
      default:
        return `#${slug}`;
    }
  }

  private extractAuthor(dbResult: any): string {
    if (dbResult.author) {
      return dbResult.author.name || dbResult.author;
    }
    return '';
  }

  private addTypeSpecificFields(result: SearchResult, dbResult: any, type: SearchResultType): void {
    const extendedResult = result as any;

    switch (type) {
      case 'team':
        extendedResult.country = dbResult.country;
        extendedResult.region = dbResult.region;
        extendedResult.game = dbResult.game;
        extendedResult.players = dbResult.players;
        extendedResult.logo = dbResult.logo;
        break;
      case 'player':
        extendedResult.gamertag = dbResult.gamertag;
        extendedResult.realName = dbResult.irlName;
        extendedResult.country = dbResult.country;
        extendedResult.role = dbResult.role;
        extendedResult.photo = dbResult.photo; // Add player photo
        extendedResult.currentTeam = dbResult.currentTeam; // Add team data
        break;
      case 'tournament':
        extendedResult.startDate = dbResult.startDate;
        extendedResult.endDate = dbResult.endDate;
        extendedResult.prizePool = dbResult.prizePool;
        extendedResult.format = dbResult.format;
        extendedResult.game = dbResult.game; // Keep the full game object
        extendedResult.image = dbResult.image; // Also add tournament image if available
        break;
      case 'match':
        extendedResult.game = dbResult.game;
        extendedResult.teams = dbResult.teams;
        break;
      case 'article':
        extendedResult.game = dbResult.game;
        extendedResult.teams = dbResult.teams?.map((team: any) => team.name);
        extendedResult.coverImage = dbResult.coverImage;
        break;
    }
  }

  private createHighlights(
    dbResult: any,
    query: string,
  ): Record<string, string> {
    const highlights: Record<string, string> = {};
    const searchTerms = query
      .toLowerCase()
      .split(' ')
      .filter((term) => term.length > 2);

    const type = this.mapGraphQLTypeToSearchType(dbResult.__typename);

    // Highlight title based on type
    const title = this.extractTitle(dbResult, type);
    highlights.title = this.highlightText(title, searchTerms);

    // Highlight excerpt based on type
    const excerpt = this.extractExcerpt(dbResult, type);
    highlights.excerpt = this.highlightText(excerpt, searchTerms);

    return highlights;
  }

  private highlightText(text: string, searchTerms: string[]): string {
    let highlighted = text;

    searchTerms.forEach((term) => {
      const regex = new RegExp(`(${this.escapeRegExp(term)})`, 'gi');
      highlighted = highlighted.replace(regex, '<mark>$1</mark>');
    });

    return highlighted;
  }

  private escapeRegExp(string: string): string {
    return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  private createExcerpt(content: string, maxLength: number = 200): string {
    if (!content) return '';

    const text = content.replace(/(<([^>]+)>)/gi, ''); // Strip HTML
    if (text.length <= maxLength) return text;

    return text.substring(0, maxLength).replace(/\s+\S*$/, '') + '...';
  }

  private buildContentTypeFilter(filters?: SearchFilters): string[] | null {
    if (!filters?.contentType || filters.contentType.length === 0) {
      return null;
    }

    return filters.contentType;
  }

  private getCacheKey(request: SearchRequest): string {
    return JSON.stringify({
      query: request.query,
      filters: request.filters,
      page: request.page,
      limit: request.limit,
    });
  }

  private getCachedResponse(cacheKey: string): SearchResponse | null {
    const cached = this.searchCache.get(cacheKey);
    if (!cached) return null;

    const isExpired = Date.now() - cached.timestamp > this.CACHE_TIMEOUT;
    if (isExpired) {
      this.searchCache.delete(cacheKey);
      return null;
    }

    return cached.response;
  }

  private cacheResponse(cacheKey: string, response: SearchResponse): void {
    this.searchCache.set(cacheKey, {
      response,
      timestamp: Date.now(),
    });

    // Clean up old cache entries periodically
    if (this.searchCache.size > 100) {
      const entries = Array.from(this.searchCache.entries());
      const sortedEntries = entries.sort(
        (a, b) => a[1].timestamp - b[1].timestamp,
      );
      const toDelete = sortedEntries.slice(0, 20);

      toDelete.forEach(([key]) => {
        this.searchCache.delete(key);
      });
    }
  }

  private trackSearchAnalytics(
    request: SearchRequest,
    response: SearchResponse,
    startTime: number,
    options: SearchOptions,
  ): void {
    if (options.enableAnalytics === false) return;

    const executionTime = Date.now() - startTime;

    if (response.results.length === 0) {
      this.metrics.track('search_no_results', {
        query: request.query,
        content_type_filter: request.filters?.contentType?.join(','),
        user_id: options.userId,
        session_id: options.sessionId,
      });
    } else {
      this.metrics.track('search_performed', {
        query: request.query,
        results_count: response.results.length,
        execution_time: executionTime,
        content_type_filter: request.filters?.contentType?.join(','),
        user_id: options.userId,
        session_id: options.sessionId,
      });
    }
  }

  private addToRecentSearches(query: string): void {
    // Remove if already exists
    this.recentSearches = this.recentSearches.filter(
      (search) => search !== query,
    );

    // Add to beginning
    this.recentSearches.unshift(query);

    // Keep only last 10
    this.recentSearches = this.recentSearches.slice(0, 10);

    // Persist to localStorage
    this.saveRecentSearches();
  }

  private loadRecentSearches(): void {
    if (typeof localStorage === 'undefined') return;

    try {
      const saved = localStorage.getItem('1vx-recent-searches');
      if (saved) {
        this.recentSearches = JSON.parse(saved);
      }
    } catch (error) {
      console.warn('Error loading recent searches:', error);
    }
  }

  private saveRecentSearches(): void {
    if (typeof localStorage === 'undefined') return;

    try {
      localStorage.setItem(
        '1vx-recent-searches',
        JSON.stringify(this.recentSearches),
      );
    } catch (error) {
      console.warn('Error saving recent searches:', error);
    }
  }
}

declare module '@ember/service' {
  interface Registry {
    search: SearchService;
  }
}
